<template>
  <div
    class="page-list-item handle"
    :class="{
      opened: $route.query.id === page.id,
      selected: selectedPageId === page.id,
      'object-node': page.kind === 'object',
    }"
    @mouseenter="onMouseEnter"
    @mouseleave="onMouseLeave"
    @click.prevent="onClick($event)"
    @dblclick.prevent="onDoubleClick($event)"
    @contextmenu.prevent.stop="onContextMenu"
  >
    <div class="item-left">
      <div
        v-if="page.children.length"
        :class="[
          { expanded: !collapsedFolders.includes(page.id) },
          'arrow-wrapper',
        ]"
        @click.prevent.stop="$emit('toggleFolder', page.id)"
        @dblclick.stop
      >
        <div class="arrow" />
      </div>
      <span v-if="page.kind === 'object' && objectIcon" class="object-icon">
        <component :is="objectIcon" v-if="typeof objectIcon !== 'string'" />
        <template v-else>{{ objectIcon }}</template>
      </span>
      <div class="title">
        <span v-if="page.title !== ''">{{ page.title }}</span>
        <span v-else class="placeholder">Untitled</span>
      </div>
    </div>
    <div v-if="isOvering && writable" class="actions-wrapper">
      <BaseButton
        kind="auxiliary"
        @click.prevent.stop="$emit('addPageTo', page.id)"
      >
        <template #icon>
          <AddIcon />
        </template>
      </BaseButton>
    </div>
  </div>
</template>
<script lang="ts">
import {
  defineComponent,
  PropType,
  ref,
  shallowRef,
  toRefs,
  watch,
  type Component,
} from "vue";

import BaseButton from "@/components/base/BaseButton.vue";
import { getObjectIcon } from "@/components/databases";
import { asGraphRecordResult } from "@/components/graph/result";
import { getTableObjectData } from "@/components/graph/utils";
import AddIcon from "@/components/icons/AddIcon.vue";
import * as inject from "@/components/inject";
import type { PagesForestNode } from "@/page";

export default defineComponent({
  components: { AddIcon, BaseButton },
  props: {
    page: { type: Object as PropType<PagesForestNode>, required: true },
    // Whether the row offers its hover "+" (subpage creation) — see
    // PagesForestPane.
    writable: { type: Boolean, default: true },
    collapsedFolders: { type: Array as PropType<string[]>, required: true },
    selectedPageId: {
      type: [String, null] as PropType<string | null>,
      required: true,
    },
  },
  emits: {
    addPageTo: (_id: string) => true,
    openPage: (
      _id: string,
      _kind: PagesForestNode["kind"],
      _event?: MouseEvent
    ) => true,
    selectPage: (
      _id: string,
      _kind: PagesForestNode["kind"],
      _event?: MouseEvent
    ) => true,
    // Right click: which menu to show depends on the data model behind the
    // node (native page vs graph row), so the pane's source handles it.
    showContextMenu: (
      _id: string,
      _kind: PagesForestNode["kind"],
      _event: MouseEvent
    ) => true,
    toggleFolder: (_id: string) => true,
  },
  setup(props, { emit }) {
    const { page } = toRefs(props);
    const controller = inject.controller();
    const profile = inject.profile();
    // A custom-object node's icon: its type's icon (or the member's avatar
    // for a member row) — `getObjectIcon` needs the object's database and
    // record, fetched once per node (shallow: the icon may be a component).
    const objectIcon = shallowRef<Component | string | null>(null);
    watch(
      () => (page.value.kind === "object" ? page.value.id : null),
      async (objectId) => {
        if (!objectId) {
          objectIcon.value = null;
          return;
        }
        const database = (
          await controller.graph.database.ofObject(objectId)
        ).release();
        const result = (await controller.graph.object.get(objectId)).release();
        // The node may have been recycled for another id while fetching.
        if (page.value.id !== objectId) return;
        const data = getTableObjectData(result);
        objectIcon.value = getObjectIcon(
          profile.value,
          database,
          data ? asGraphRecordResult(data) : null
        );
      },
      { immediate: true }
    );
    // A single click selects the node (persistent highlight, see the
    // `selected` class) and previews it in the right pane; a double click
    // opens it full screen (its own route).
    const onClick = (event: MouseEvent) => {
      emit("selectPage", page.value.id, page.value.kind, event);
    };
    const onDoubleClick = (event: MouseEvent) => {
      emit("openPage", page.value.id, page.value.kind, event);
    };
    const onContextMenu = (event: MouseEvent) => {
      emit("showContextMenu", page.value.id, page.value.kind, event);
    };
    const isOvering = ref(false);
    const onMouseEnter = () => {
      isOvering.value = true;
    };
    const onMouseLeave = () => {
      isOvering.value = false;
    };
    return {
      isOvering,
      objectIcon,
      onClick,
      onContextMenu,
      onDoubleClick,
      onMouseEnter,
      onMouseLeave,
    };
  },
});
</script>
<style scoped lang="scss">
.page-list-item {
  display: flex;
  flex-direction: row;
  align-items: center;
  position: relative;
  padding: 6px 8px;
  border-radius: $border-radius-m;
  justify-content: space-between;
  width: calc(100% - 16px);

  @include themed() {
    color: t($grey-60);
  }

  &:hover,
  &.opened {
    @include themed() {
      background: t($bg-menu-hover);
    }
  }

  &.opened {
    @include themed() {
      color: t($primary);
    }
  }

  // The last-clicked page: a persistent highlight, one step darker than the
  // transient hover background (after `:hover` so it wins while hovered).
  &.selected {
    @include themed() {
      background: t($grey-15);
    }
  }

  &:active {
    @include themed() {
      background: t($grey-15);
    }
  }

  .item-left {
    display: flex;
    flex-direction: row;
    align-items: center;
    column-gap: 6px;
    min-width: 0;

    .base-button {
      padding: 2px;
    }

    .handle {
      cursor: grab;
      opacity: 0;
      transition: opacity 0.2s ease;

      &:active {
        cursor: grabbing;
      }

      :deep(svg) {
        width: 12px;
        height: 12px;
      }
    }

    &:hover .handle {
      opacity: 1;
    }

    .arrow-wrapper {
      .arrow {
        display: inline-block;
        width: 0;
        height: 0;
        border-radius: 2px;
        border-style: solid;
        border-width: 5px 0 5px 8px;

        @include themed() {
          border-color: transparent transparent transparent
            rgba(t($primary), 0.3);
        }
      }

      &:hover {
        .arrow {
          @include themed() {
            border-color: transparent transparent transparent
              rgba(t($primary), 0.5);
          }
        }
      }

      &.expanded {
        transform: rotate(90deg);
      }
    }

    // Custom-object rows: the object's type icon (or member avatar, or
    // emoji) marks the node as an object host (pages have no icon).
    .object-icon {
      flex-shrink: 0;
      width: 14px;
      height: 14px;
      display: inline-flex;
      align-items: center;
      justify-content: center;
      // Emoji icons come as plain text.
      font-size: 12px;
      line-height: 1;

      :deep(svg),
      :deep(img) {
        width: 14px;
        height: 14px;
      }
    }

    .title {
      text-overflow: ellipsis;
      overflow: hidden;
      @extend %font-body-regular;
      cursor: default;
      @include transition(all 0.1s ease-out);

      span.placeholder {
        @include themed() {
          color: t($grey-40);
        }
      }

      span {
        white-space: nowrap;
      }
    }
  }

  .actions-wrapper {
    display: flex;
    flex-shrink: 0;

    button.base-button.auxiliary.icon-only {
      padding: 4px;

      :deep(.icon) {
        height: 10px;
        width: 10px;

        svg {
          height: 10px;
          width: 10px;
        }
      }
    }

    &:hover
      :deep(
        button.base-button.none.auxiliary:hover:not(.disabled):not(.loading)
      ) {
      @include themed() {
        background: t($grey-15);
      }
    }
  }
}
</style>
